// src/components/AnalyticsDashboard.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./AnalyticsDashboard.css";

const AnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  // Prevent eslint warning about unused selectedSession - will be used for session details modal
  console.log("Selected session:", selectedSession);

  // Memoize mock data to prevent unnecessary recreations
  const mockAnalytics = useMemo(
    () => ({
      overview: {
        totalEnhancements: 142,
        successRate: "94.4",
        averageConfidence: "72.3",
        averageProcessingTime: 1847,
        activeSessions: 0,
      },
      sourcePerformance: {
        ai: {
          successRate: "89.2",
          averageTime: 2134,
          totalRequests: 142,
        },
        external: {
          successRate: "96.8",
          averageTime: 892,
          totalRequests: 142,
        },
        fallback: {
          successRate: "100.0",
          averageTime: 245,
          totalRequests: 8,
        },
      },
      categoryStats: {
        wine: {
          total: 67,
          successful: 64,
          totalTime: 123450,
          totalConfidence: 48.2,
        },
        beer: {
          total: 45,
          successful: 44,
          totalTime: 89234,
          totalConfidence: 31.8,
        },
        spirit: {
          total: 21,
          successful: 20,
          totalTime: 41567,
          totalConfidence: 14.2,
        },
        cocktail: {
          total: 9,
          successful: 9,
          totalTime: 18934,
          totalConfidence: 6.8,
        },
      },
      recentSessions: [
        {
          id: "session_1703798400000_abc123",
          beverage: "2019 Caymus Cabernet Sauvignon",
          category: "wine",
          success: true,
          confidence: "84.2%",
          processingTime: "1,847ms",
          timestamp: "2024-12-28T20:00:00.000Z",
        },
        {
          id: "session_1703798300000_def456",
          beverage: "Pliny the Elder",
          category: "beer",
          success: true,
          confidence: "76.8%",
          processingTime: "2,134ms",
          timestamp: "2024-12-28T19:58:20.000Z",
        },
        {
          id: "session_1703798200000_ghi789",
          beverage: "Negroni",
          category: "cocktail",
          success: true,
          confidence: "91.5%",
          processingTime: "1,203ms",
          timestamp: "2024-12-28T19:56:40.000Z",
        },
      ],
      dailyTrends: [
        { date: "2024-12-22", enhancements: 18, successes: 17, failures: 1 },
        { date: "2024-12-23", enhancements: 23, successes: 22, failures: 1 },
        { date: "2024-12-24", enhancements: 15, successes: 14, failures: 1 },
        { date: "2024-12-25", enhancements: 8, successes: 8, failures: 0 },
        { date: "2024-12-26", enhancements: 28, successes: 26, failures: 2 },
        { date: "2024-12-27", enhancements: 31, successes: 30, failures: 1 },
        { date: "2024-12-28", enhancements: 19, successes: 18, failures: 1 },
      ],
      topErrors: [
        {
          error: "Anthropic API error: 401",
          count: 234,
          lastSeen: "2024-12-28T19:45:00.000Z",
        },
        {
          error: "Gemini API key not configured",
          count: 156,
          lastSeen: "2024-12-28T19:50:00.000Z",
        },
        {
          error: "Rate limit exceeded",
          count: 23,
          lastSeen: "2024-12-28T19:30:00.000Z",
        },
        {
          error: "External API timeout",
          count: 12,
          lastSeen: "2024-12-28T19:20:00.000Z",
        },
        {
          error: "Invalid beverage format",
          count: 8,
          lastSeen: "2024-12-28T19:10:00.000Z",
        },
      ],
    }),
    []
  );

  const mockRecommendations = useMemo(
    () => [
      {
        type: "warning",
        area: "AI Services",
        message:
          "AI success rate is 89.2%. Consider checking API keys and rate limits.",
        priority: "high",
      },
      {
        type: "info",
        area: "Data Quality",
        message:
          "Average confidence is 72.3%. Fallback service is providing good coverage.",
        priority: "low",
      },
    ],
    []
  );

  useEffect(() => {
    // Load initial data
    setAnalytics(mockAnalytics);
    setRecommendations(mockRecommendations);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        // In real app: refreshAnalytics();
        console.log("📊 Refreshing analytics data...");
      }, 5000);
      setRefreshInterval(interval);
    } else if (refreshInterval) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh]);

  const refreshAnalytics = useCallback(() => {
    // In real app would call: masterEnhancementService.getEnhancementAnalytics()
    setAnalytics({ ...mockAnalytics });
    setRecommendations([...mockRecommendations]);
  }, [mockAnalytics, mockRecommendations]);

  const exportData = useCallback(
    (format) => {
      // In real app would call: masterEnhancementService.exportAnalyticsData(format)
      const data =
        format === "csv" ? "CSV data..." : JSON.stringify(analytics, null, 2);
      const blob = new Blob([data], {
        type: format === "csv" ? "text/csv" : "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `analytics_${
        new Date().toISOString().split("T")[0]
      }.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    },
    [analytics]
  );

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high":
        return "🔴";
      case "medium":
        return "🟡";
      case "low":
        return "🟢";
      default:
        return "⚪";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "warning":
        return "⚠️";
      case "error":
        return "❌";
      case "info":
        return "ℹ️";
      case "performance":
        return "🚀";
      default:
        return "📊";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!analytics) {
    return (
      <div className="analytics-dashboard loading">
        <div className="loading-spinner">📊 Loading Analytics...</div>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>📊 SommelAI Enhancement Analytics</h1>
          <p>
            Real-time insights into beverage enhancement pipeline performance
          </p>
        </div>
        <div className="header-actions">
          <button
            className={`refresh-btn ${autoRefresh ? "active" : ""}`}
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            {autoRefresh ? "⏸️ Auto-refresh ON" : "▶️ Auto-refresh OFF"}
          </button>
          <button className="refresh-btn" onClick={refreshAnalytics}>
            🔄 Refresh Now
          </button>
          <div className="export-dropdown">
            <button className="export-btn">📥 Export</button>
            <div className="export-menu">
              <button onClick={() => exportData("json")}>JSON</button>
              <button onClick={() => exportData("csv")}>CSV</button>
            </div>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="overview-grid">
        <div className="metric-card">
          <div className="metric-icon">🎯</div>
          <div className="metric-content">
            <h3>Total Enhancements</h3>
            <div className="metric-value">
              {analytics.overview.totalEnhancements.toLocaleString()}
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">✅</div>
          <div className="metric-content">
            <h3>Success Rate</h3>
            <div className="metric-value">
              {analytics.overview.successRate}%
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <h3>Avg. Confidence</h3>
            <div className="metric-value">
              {analytics.overview.averageConfidence}%
            </div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">⚡</div>
          <div className="metric-content">
            <h3>Avg. Processing</h3>
            <div className="metric-value">
              {analytics.overview.averageProcessingTime}ms
            </div>
          </div>
        </div>
      </div>

      {/* Source Performance */}
      <div className="section">
        <h2>🔧 Data Source Performance</h2>
        <div className="source-grid">
          {Object.entries(analytics.sourcePerformance).map(
            ([source, metrics]) => (
              <div key={source} className="source-card">
                <div className="source-header">
                  <h3>
                    {source === "ai" && "🤖 AI Services"}
                    {source === "external" && "🌐 External APIs"}
                    {source === "fallback" && "🔄 Fallback"}
                  </h3>
                </div>
                <div className="source-metrics">
                  <div className="source-metric">
                    <span className="metric-label">Success Rate</span>
                    <span className="metric-value">{metrics.successRate}%</span>
                  </div>
                  <div className="source-metric">
                    <span className="metric-label">Avg. Time</span>
                    <span className="metric-value">
                      {metrics.averageTime}ms
                    </span>
                  </div>
                  <div className="source-metric">
                    <span className="metric-label">Total Requests</span>
                    <span className="metric-value">
                      {metrics.totalRequests}
                    </span>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {/* Category Statistics */}
      <div className="section">
        <h2>📊 Category Breakdown</h2>
        <div className="category-grid">
          {Object.entries(analytics.categoryStats).map(([category, stats]) => {
            const avgConfidence =
              stats.total > 0
                ? ((stats.totalConfidence / stats.total) * 100).toFixed(1)
                : 0;
            const avgTime =
              stats.total > 0 ? Math.round(stats.totalTime / stats.total) : 0;

            return (
              <div key={category} className="category-card">
                <div className="category-header">
                  <h3>
                    {category === "wine" && "🍷 Wine"}
                    {category === "beer" && "🍺 Beer"}
                    {category === "spirit" && "🥃 Spirits"}
                    {category === "cocktail" && "🍸 Cocktails"}
                  </h3>
                </div>
                <div className="category-stats">
                  <div className="stat-row">
                    <span>Total</span>
                    <span>{stats.total}</span>
                  </div>
                  <div className="stat-row">
                    <span>Success Rate</span>
                    <span>
                      {((stats.successful / stats.total) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="stat-row">
                    <span>Avg. Confidence</span>
                    <span>{avgConfidence}%</span>
                  </div>
                  <div className="stat-row">
                    <span>Avg. Time</span>
                    <span>{avgTime}ms</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Sessions */}
      <div className="section">
        <h2>🕒 Recent Enhancement Sessions</h2>
        <div className="sessions-table">
          <div className="table-header">
            <span>Beverage</span>
            <span>Category</span>
            <span>Status</span>
            <span>Confidence</span>
            <span>Time</span>
            <span>Timestamp</span>
          </div>
          {analytics.recentSessions.map((session) => (
            <div
              key={session.id}
              className="table-row"
              onClick={() => setSelectedSession(session)}
            >
              <span className="beverage-name">{session.beverage}</span>
              <span className="category">
                {session.category === "wine" && "🍷"}
                {session.category === "beer" && "🍺"}
                {session.category === "spirit" && "🥃"}
                {session.category === "cocktail" && "🍸"}
                {session.category}
              </span>
              <span
                className={`status ${session.success ? "success" : "failed"}`}
              >
                {session.success ? "✅ Success" : "❌ Failed"}
              </span>
              <span className="confidence">{session.confidence}</span>
              <span className="processing-time">{session.processingTime}</span>
              <span className="timestamp">
                {formatTimestamp(session.timestamp)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Daily Trends */}
      <div className="section">
        <h2>📈 7-Day Trends</h2>
        <div className="trends-chart">
          {analytics.dailyTrends.map((day) => {
            const successRate =
              day.enhancements > 0
                ? (day.successes / day.enhancements) * 100
                : 0;
            return (
              <div key={day.date} className="trend-day">
                <div className="trend-bar">
                  <div
                    className="trend-success"
                    style={{ height: `${Math.max(successRate, 5)}%` }}
                  ></div>
                  <div
                    className="trend-failure"
                    style={{ height: `${Math.max(100 - successRate, 5)}%` }}
                  ></div>
                </div>
                <div className="trend-label">
                  <div className="trend-date">{formatDate(day.date)}</div>
                  <div className="trend-count">{day.enhancements}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="section">
          <h2>💡 Performance Recommendations</h2>
          <div className="recommendations-list">
            {recommendations.map((rec, index) => (
              <div key={index} className={`recommendation ${rec.priority}`}>
                <div className="recommendation-icon">
                  {getPriorityIcon(rec.priority)} {getTypeIcon(rec.type)}
                </div>
                <div className="recommendation-content">
                  <div className="recommendation-area">{rec.area}</div>
                  <div className="recommendation-message">{rec.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Errors */}
      <div className="section">
        <h2>🚨 Common Issues</h2>
        <div className="errors-list">
          {analytics.topErrors.map((error, index) => (
            <div key={index} className="error-item">
              <div className="error-info">
                <div className="error-message">{error.error}</div>
                <div className="error-meta">
                  Last seen: {formatTimestamp(error.lastSeen)}
                </div>
              </div>
              <div className="error-count">{error.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(AnalyticsDashboard);
